// ==================== User & Authentication Types ====================

export interface User {
  id: string;
  email: string;
  company_name: string;
  first_name?: string;
  last_name?: string;
  role: 'ADMIN' | 'ENTERPRISE' | 'USER';
  is_verified: boolean;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  confirm_password: string;
  company_name: string;
  first_name?: string;
  last_name?: string;
}

export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  company_name?: string;
}

export interface ChangePasswordData {
  current_password: string;
  new_password: string;
}

// ==================== Subscription Types ====================
// Re-export from subscription.ts (NIB types - backend source of truth)
export type {
  SubscriptionTier,
  SubscriptionStatus,
  BillingPeriod,
  Subscription,
  SubscriptionTierInfo,
  UsageStats,
  CurrentPeriodStats,
  MonthlyHistoryStats,
  LimitCheckResponse,
  BillingHistoryItem,
} from './subscription';

// Re-export helper functions
export {
  isUnlimitedTier,
  formatEmployeeLimit,
  getUsagePercentage,
  getUsageColorClass,
  getNextTier,
  TIER_FALLBACK,
  TIER_COLORS,
} from './subscription';

// Legacy converter-based usage stats (for backward compatibility with converter features)
export interface ConverterUsageStats {
  current_period: {
    total_conversions: number;
    successful_conversions: number;
    failed_conversions: number;
    monthly_limit: number;
    usage_percentage: number;
    remaining_conversions: number;
    period_start: string;
    period_end: string;
  };
  by_source_system: Record<SourceSystem, number>;
  history: ConverterMonthlyUsage[];
}

export interface ConverterMonthlyUsage {
  month: string;
  total: number;
  successful: number;
  failed: number;
}

export interface UsageLimitCheck {
  can_convert: boolean;
  remaining_conversions: number;
  usage_percentage: number;
  limit_reached: boolean;
}

export interface ApiKeyResponse {
  api_key: string;
  masked_key: string;
  created_at: string;
  old_key_masked?: string;
  warning?: string;
}

// ==================== Converter Types ====================

export type SourceSystem =
  | 'QUICKBOOKS'
  | 'ADP'
  | 'PAYCHEX'
  | 'GUSTO'
  | 'WORKDAY';

export type ConversionStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export type OutputFormat = 'V3_STANDARD' | 'JSON' | 'CSV';

export interface Conversion {
  id: string;
  user_id: string;
  subscription_id: string;
  source_system: SourceSystem;
  status: ConversionStatus;
  file_name: string;
  record_count?: number;
  estimated_records?: number;
  started_at: string;
  created_at: string;
  completed_at?: string;
  processing_time_seconds?: number;
  output_file_id?: string;
  error_message?: string;
}

export interface ConversionRequest {
  file: File;
  source_system: SourceSystem;
  mapping_config_id?: string;
  output_format?: OutputFormat;
}

export interface BatchConversionRequest {
  files: File[];
  source_system: SourceSystem;
  mapping_config_id?: string;
}

export interface BatchConversion {
  batch_id: string;
  total_files: number;
  conversions: Array<{
    conversion_id: string;
    file_name: string;
    status: ConversionStatus;
  }>;
}

export interface ConversionHistory {
  conversions: Conversion[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ConversionFilters {
  page?: number;
  per_page?: number;
  status?: ConversionStatus;
  source_system?: SourceSystem;
  start_date?: string;
  end_date?: string;
  search?: string;
}

export interface ValidationResult {
  field: string;
  message: string;
  severity: 'error' | 'warning';
  row_number?: number;
}

export interface TransformationResult {
  field: string;
  original_value: string;
  transformed_value: string;
  transformation_applied: string;
}

export interface ConversionDetails extends Conversion {
  file_size?: number;
  file_size_human?: string;
  mime_type?: string;
  validation_results?: {
    errors: ValidationResult[];
    warnings: ValidationResult[];
    total_errors: number;
    total_warnings: number;
  };
  transformation_results?: {
    fields_mapped: number;
    records_processed: number;
    transformations: TransformationResult[];
  };
  processing_timeline?: {
    uploaded_at: string;
    started_processing_at?: string;
    validation_completed_at?: string;
    transformation_completed_at?: string;
    completed_at?: string;
  };
}

// ==================== Mapping Types ====================

export type TransformationType =
  | 'none'
  | 'uppercase'
  | 'lowercase'
  | 'title_case'
  | 'trim'
  | 'date_format'
  | 'number_format'
  | 'replace'
  | 'prefix'
  | 'suffix'
  | 'concatenate'
  | 'split';

// Transformation parameter types for different transformation types
export type TransformationParams = {
  pattern?: string; // date_format
  decimals?: number; // number_format
  old_value?: string; // replace
  new_value?: string; // replace
  value?: string; // prefix, suffix
  separator?: string; // concatenate
  delimiter?: string; // split
  index?: number; // split
};

export interface FieldMapping {
  source_field: string;
  target_field: string;
  transformation_rule?: TransformationType;
  transformation_params?: TransformationParams;
}

export interface MappingConfig {
  id: string;
  user_id: string;
  name: string;
  source_system: SourceSystem;
  field_mappings: Record<string, FieldMapping>;
  is_default: boolean;
  inserted_date: string;
  updated_date: string;
}

export interface MappingRequest {
  name: string;
  source_system: SourceSystem;
  field_mappings: Record<string, FieldMapping>;
  is_default: boolean;
}

export interface MappingList {
  mappings: Array<{
    id: string;
    name: string;
    source_system: SourceSystem;
    is_default: boolean;
    field_count: number;
    created_at: string;
    updated_at: string;
  }>;
  total: number;
  page: number;
  per_page: number;
}

// ==================== File Management Types ====================

export type FileType = 'SOURCE' | 'CONVERTED' | 'TEMPORARY';
export type FileStatus = 'UPLOADED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

export interface FileRecord {
  id: string;
  user_id: string;
  conversion_id?: string;
  original_name: string;
  stored_name: string;
  file_size: number;
  file_size_human: string;
  mime_type: string;
  checksum: string;
  file_type: FileType;
  status: FileStatus;
  created_at: string;
}

export interface FileList {
  files: FileRecord[];
  total: number;
  page: number;
  per_page: number;
}

export interface StorageStats {
  total_files: number;
  total_size_bytes: number;
  total_size_human: string;
  by_type: Record<FileType, number>;
  by_status: Record<FileStatus, number>;
}

// ==================== API Response Types ====================

export type ApiResponse<T> =
  | {
      success: true;
      message?: string;
      data: T;
    }
  | {
      success: false;
      message?: string;
      error: string;
      details?: Record<string, unknown>;
    };

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ApiError {
  success: false;
  error: string;
  message: string;
  details?: Record<string, unknown>;
}

// ==================== Pagination & Query Types ====================

export interface PaginationParams {
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface FileQueryParams extends PaginationParams {
  file_type?: FileType;
  status?: FileStatus;
}

// ==================== Dashboard Types ====================

export interface DashboardStats {
  total_conversions: number;
  successful_conversions: number;
  failed_conversions: number;
  usage_percentage: number;
  recent_conversions: Conversion[];
}

// ==================== UI Component Types ====================

export interface FileUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadedFile {
  file: File;
  preview?: string;
  error?: string;
}

// ==================== NIB C10 Types ====================

// NIB Format Types
export type NIBFormatType = 'NON_HOSPITALITY' | 'HOSPITALITY';

export type NIBSubmissionStatus =
  | 'DRAFT'
  | 'VALIDATING'
  | 'VALIDATED'
  | 'GENERATING'
  | 'GENERATED'
  | 'SUBMITTED'
  | 'FAILED';

export type NIBEmployeeType = 'REGULAR' | 'SUMMER_STUDENT' | 'RETIREE';

export type NIBSourceSystem = 'QUICKBOOKS' | 'ADP';

export type NIBPayIndicator = 'W' | 'B' | 'S' | 'M'; // Weekly, Bi-weekly, Semi-monthly, Monthly

// NIB Submission
export interface NIBSubmission {
  id: string;
  user_id: string;
  employer_number: string;
  employer_name: string;
  contribution_period: string; // MMYY format
  period_year: number;
  period_month: number;
  format_type: NIBFormatType;
  status: NIBSubmissionStatus;
  total_records: number;
  total_insurable_wages: number;
  total_contributions: number;
  total_employee_contributions: number;
  total_employer_contributions: number;
  total_gratuity_contributions: number;
  is_validated: boolean;
  validated_at?: string;
  generated_at?: string;
  submitted_at?: string;
  output_file_path?: string;
  source_file_path?: string;
  error_message?: string;
  inserted_date: string;
  updated_date: string;
}

// NIB Employee Record
export interface NIBEmployeeRecord {
  id: string;
  submission_id: string;
  employee_ni_number: string; // 8 digits
  last_name: string;
  first_name: string;
  middle_initial?: string;
  category: NIBCategory; // 1-7 (July 2024 category system)
  is_hospitality: boolean;
  original_gross_wages: number;
  original_gratuity: number;
  insurable_wages: number;
  capped_wages: number;
  gratuity_wages: number;
  employee_contribution: number;
  employer_contribution: number;
  gratuity_contribution: number;
  total_contribution: number;
  weeks_worked: number;
  pay_indicator: NIBPayIndicator;
  wage_was_truncated: boolean;
  wage_was_capped: boolean;
  is_valid: boolean;
  validation_errors?: string[];
  row_number?: number;
  inserted_date: string;
  updated_date: string;
}

// NIB Validation Types
export interface NIBValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
  code?: string;
}

export interface NIBRecordValidationError {
  record_id: string;
  row_number: number;
  employee_name: string;
  ni_number: string;
  errors: NIBValidationError[];
}

export interface NIBValidationResult {
  submission_id: string;
  is_valid: boolean;
  total_records: number;
  valid_records: number;
  invalid_records: number;
  total_errors: number;
  total_warnings: number;
  truncated_wages_count: number;
  capped_wages_count: number;
  record_errors: NIBRecordValidationError[];
  submission_errors: NIBValidationError[];
  can_generate: boolean;
}

// NIB Calculation Preview Types
export interface NIBContributionPreview {
  gross_wages: number;
  insurable_wages: number;
  employee_contribution: number;
  employer_contribution: number;
  gratuity?: number;
  gratuity_contribution: number;
  total_contribution: number;
  wage_was_truncated: boolean;
  wage_was_capped: boolean;
  calculation_details: {
    employee_rate: number; // 3.9%
    employer_rate: number; // 5.9%
    gratuity_rate: number; // 9.8%
    weekly_cap: number; // $650
    monthly_cap: number; // $2,817
  };
}

export interface NIBWagePreview {
  original_wage: string;
  truncated_wage: string;
  capped_wage: string;
  was_truncated: boolean;
  was_capped: boolean;
  pay_indicator?: string;
  weeks?: number;
}

// NIB Request Types
export interface NIBSubmissionCreateRequest {
  employer_number: string;
  employer_name?: string;
  contribution_period: string; // MMYY format
  format_type: NIBFormatType;
  pay_indicator?: NIBPayIndicator;
}

export interface NIBRecordCreateRequest {
  employee_ni_number: string;
  last_name: string;
  first_name: string;
  middle_initial?: string;
  employee_type?: NIBEmployeeType;
  original_gross_wages: number;
  original_gratuity?: number;
  weeks_worked: number;
}

export interface NIBImportRequest {
  source_system: NIBSourceSystem;
  source_file_path: string;
  pay_indicator?: NIBPayIndicator;
}

// NIB Response Types
export interface NIBSubmissionFilters {
  page?: number;
  per_page?: number;
  status?: NIBSubmissionStatus;
  period_year?: number;
  period_month?: number;
  search?: string;
  sort_by?: 'created' | 'period' | 'status' | 'records';
  sort_order?: 'asc' | 'desc';
}

export interface NIBSubmissionList {
  items: NIBSubmission[]; // Backend returns 'items' not 'submissions'
  total: number;
  page: number;
  per_page: number;
  pages: number; // Backend returns 'pages' not 'total_pages'
}

export interface NIBSubmissionDetail extends NIBSubmission {
  records: NIBEmployeeRecord[];
  validation_result?: NIBValidationResult;
}

export interface NIBNewHire {
  ni_number: string;
  last_name: string;
  first_name: string;
  original_gross_wages: number;
  original_gratuity: number;
  employee_type: string;
  weeks_worked: number;
  pay_indicator: string;
  insurable_wages: number;
  capped_wages: number;
  employee_contribution: number;
  employer_contribution: number;
  total_contribution: number;
}

export interface NIBMergeResult {
  matched: number;
  updated_records: Array<{
    record_id: number;
    ni_number: string;
    name: string;
    wages: number;
  }>;
  new_hires: NIBNewHire[];
  missing_from_file: Array<{
    record_id: number;
    ni_number: string;
    name: string;
  }>;
  total_in_file: number;
  total_in_submission: number;
}

export interface NIBImportResult {
  submission_id: string;
  total_rows: number;
  imported_count: number;
  skipped_count: number;
  errors: Array<{
    row: number;
    field?: string;
    message: string;
  }>;
}

export interface NIBGenerateResult {
  submission_id: string;
  output_file_path: string;
  file_size: number;
  record_count: number;
  generated_at: string;
}

// NIB Contribution Preview Request (matches backend)
export interface NIBContributionPreviewRequest {
  category: number; // NIBCategory 1-7
  insurable_wages: number;
  gratuity_wages?: number;
}

// NIB Wage Preview Request (matches backend)
export interface NIBWagePreviewRequest {
  category: number; // NIBCategory 1-7
  original_wage: number;
  pay_indicator: NIBPayIndicator;
  weeks_worked: number; // Note: backend uses weeks_worked not weeks
}

// NIB Calculator Input (frontend form state)
export interface NIBContributionCalculatorInput {
  original_wage: number;
  weeks: number;
  pay_indicator: NIBPayIndicator;
  category: number; // NIBCategory 1-7 (replaces old employee_type)
  gratuity_wages?: number;
}

// ==================== NIB Category Enum ====================

export enum NIBCategory {
  REGULAR = 1, // Regular employees under 65
  RETIRED = 2, // Retired employees 65+
  SENIOR_65_69 = 3, // Senior employees 65-69 (not retired)
  RETIRED_65_69 = 4, // Retired employees 65-69
  SENIOR_70_PLUS = 5, // Senior employees 70+ (not retired)
  RETIRED_70_PLUS = 6, // Retired employees 70+
  SUMMER_STUDENT = 7, // Summer students
}

// ==================== NIB Constants - UPDATED July 2024 ====================

export const NIB_RATES = {
  // Category 1: Regular Employees (Under 65)
  CAT_1_EMPLOYEE_RATE: 0.0465, // 4.65% (was 3.9%)
  CAT_1_EMPLOYER_RATE: 0.0665, // 6.65% (was 5.9%)
  CAT_1_TOTAL_RATE: 0.113, // 11.30%

  // Category 2: Retired Employees (65+)
  CAT_2_EMPLOYEE_RATE: 0, // 0%
  CAT_2_EMPLOYER_RATE: 0.02, // 2%
  CAT_2_TOTAL_RATE: 0.02, // 2%

  // Category 3: Senior Employees (65-69, not retired)
  CAT_3_EMPLOYEE_RATE: 0.0415, // 4.15%
  CAT_3_EMPLOYER_RATE: 0.0615, // 6.15%
  CAT_3_TOTAL_RATE: 0.103, // 10.30%

  // Category 4: Retired Employees 65-69
  CAT_4_EMPLOYEE_RATE: 0, // 0%
  CAT_4_EMPLOYER_RATE: 0.02, // 2%
  CAT_4_TOTAL_RATE: 0.02, // 2%

  // Category 5: Senior Employees (70+, not retired)
  CAT_5_EMPLOYEE_RATE: 0.0415, // 4.15%
  CAT_5_EMPLOYER_RATE: 0.0615, // 6.15%
  CAT_5_TOTAL_RATE: 0.103, // 10.30%

  // Category 6: Retired Employees 70+
  CAT_6_EMPLOYEE_RATE: 0, // 0%
  CAT_6_EMPLOYER_RATE: 0.02, // 2%
  CAT_6_TOTAL_RATE: 0.02, // 2%

  // Category 7: Summer Students
  CAT_7_EMPLOYEE_RATE: 0, // 0%
  CAT_7_EMPLOYER_RATE: 0.02, // 2%
  CAT_7_TOTAL_RATE: 0.02, // 2%

  // Gratuity Rate (Hospitality industry, non-retirees only)
  GRATUITY_RATE: 0.098, // 9.8%
} as const;

export const NIB_WAGE_CAPS = {
  // Standard employee ceilings (Categories 1, 3, 5, 7)
  WEEKLY_WAGE_CAP: 810.0, // $810/week (was $650)
  MONTHLY_WAGE_CAP: 3510.0, // $3,510/month (was $2,817)

  // Retiree ceilings (Categories 2, 4, 6)
  WEEKLY_WAGE_CAP_RETIRED: 405.0, // $405/week (50% of standard)
  MONTHLY_WAGE_CAP_RETIRED: 1755.0, // $1,755/month (50% of standard)
} as const;

export const NIB_C10_FORMAT = {
  // Record lengths
  NON_HOSPITALITY_RECORD_LENGTH: 79, // Standard C10 record (was 100)
  HOSPITALITY_RECORD_LENGTH: 115, // Hospitality C10 record (79 + 36 extension)

  // Field positions (1-indexed as per NIB spec)
  RECORD_ID_POS: 1,
  CONTRIBUTION_PERIOD_POS: 10,
  EMPLOYER_NUMBER_POS: 17,
  LAST_NAME_POS: 27,
  FIRST_NAME_POS: 44,
  EMPLOYEE_NI_NUMBER_POS: 59,
  TOTAL_INSURABLE_WAGES_POS: 67,
  TOTAL_CONTRIBUTIONS_POS: 73,
  WEEKS_WORKED_POS: 78,
  PAY_INDICATOR_POS: 79,

  // Hospitality extension positions (positions 101-115)
  BASIC_WAGE_POS: 101,
  GRATUITY_WAGE_POS: 106,
  GRATUITY_CONTRIBUTIONS_POS: 111,
} as const;

export const NIB_VALIDATION = {
  // Weeks worked
  MIN_WEEKS_WORKED: 1,
  MAX_WEEKS_WEEKLY: 5, // Maximum weeks for weekly pay
  MAX_WEEKS_MONTHLY: 99, // Maximum weeks for monthly pay

  // Categories
  MIN_CATEGORY: 1,
  MAX_CATEGORY: 7,

  // NI Number
  NI_NUMBER_LENGTH: 8, // 8 digits for Bahamian NI numbers

  // Name fields
  MAX_LAST_NAME_LENGTH: 17,
  MAX_FIRST_NAME_LENGTH: 15,

  // Other
  MAX_WEEKS_PER_MONTH: 5,
  RETIREE_THRESHOLD: 325, // $325
} as const;

// Legacy constants for backward compatibility
export const NIB_CONSTANTS = {
  ...NIB_RATES,
  ...NIB_WAGE_CAPS,
  ...NIB_C10_FORMAT,
  ...NIB_VALIDATION,

  // Deprecated - use category-specific rates
  EMPLOYEE_RATE: 0.0465, // For backwards compatibility (Cat 1)
  EMPLOYER_RATE: 0.0665, // For backwards compatibility (Cat 1)
  SUMMER_STUDENT_EMPLOYER_RATE: 0.02, // 2%
} as const;

// ==================== Category Metadata ====================

export interface NIBCategoryMetadata {
  name: string;
  description: string;
  employeeRate: number;
  employerRate: number;
  totalRate: number;
  weeklyCap: number;
  monthlyCap: number;
  isRetired: boolean;
  gratuityEligible: boolean;
}

export const NIB_CATEGORY_INFO: Record<NIBCategory, NIBCategoryMetadata> = {
  [NIBCategory.REGULAR]: {
    name: 'Regular Employees',
    description: 'Employees under 65 years old',
    employeeRate: 0.0465,
    employerRate: 0.0665,
    totalRate: 0.113,
    weeklyCap: 810.0,
    monthlyCap: 3510.0,
    isRetired: false,
    gratuityEligible: true,
  },
  [NIBCategory.RETIRED]: {
    name: 'Retired Employees',
    description: 'Retired employees 65+ years old',
    employeeRate: 0,
    employerRate: 0.02,
    totalRate: 0.02,
    weeklyCap: 405.0,
    monthlyCap: 1755.0,
    isRetired: true,
    gratuityEligible: false,
  },
  [NIBCategory.SENIOR_65_69]: {
    name: 'Senior Employees (65-69)',
    description: 'Employees 65-69 years old (not retired)',
    employeeRate: 0.0415,
    employerRate: 0.0615,
    totalRate: 0.103,
    weeklyCap: 810.0,
    monthlyCap: 3510.0,
    isRetired: false,
    gratuityEligible: true,
  },
  [NIBCategory.RETIRED_65_69]: {
    name: 'Retired Employees (65-69)',
    description: 'Retired employees 65-69 years old',
    employeeRate: 0,
    employerRate: 0.02,
    totalRate: 0.02,
    weeklyCap: 405.0,
    monthlyCap: 1755.0,
    isRetired: true,
    gratuityEligible: false,
  },
  [NIBCategory.SENIOR_70_PLUS]: {
    name: 'Senior Employees (70+)',
    description: 'Employees 70+ years old (not retired)',
    employeeRate: 0.0415,
    employerRate: 0.0615,
    totalRate: 0.103,
    weeklyCap: 810.0,
    monthlyCap: 3510.0,
    isRetired: false,
    gratuityEligible: true,
  },
  [NIBCategory.RETIRED_70_PLUS]: {
    name: 'Retired Employees (70+)',
    description: 'Retired employees 70+ years old',
    employeeRate: 0,
    employerRate: 0.02,
    totalRate: 0.02,
    weeklyCap: 405.0,
    monthlyCap: 1755.0,
    isRetired: true,
    gratuityEligible: false,
  },
  [NIBCategory.SUMMER_STUDENT]: {
    name: 'Summer Students',
    description: 'Summer student employees',
    employeeRate: 0,
    employerRate: 0.02,
    totalRate: 0.02,
    weeklyCap: 810.0,
    monthlyCap: 3510.0,
    isRetired: false,
    gratuityEligible: false,
  },
};

// Category helper sets
export const RETIRED_CATEGORIES = new Set<NIBCategory>([
  NIBCategory.RETIRED,
  NIBCategory.RETIRED_65_69,
  NIBCategory.RETIRED_70_PLUS,
]);

export const ZERO_EMPLOYEE_RATE_CATEGORIES = new Set<NIBCategory>([
  NIBCategory.RETIRED,
  NIBCategory.RETIRED_65_69,
  NIBCategory.RETIRED_70_PLUS,
  NIBCategory.SUMMER_STUDENT,
]);

// Legacy category rate lookup (deprecated - use NIB_CATEGORY_INFO)
export const NIB_CATEGORY_RATES: Record<
  number,
  { employee: number; employer: number }
> = {
  1: { employee: 0.0465, employer: 0.0665 }, // Regular (<65)
  2: { employee: 0, employer: 0.02 }, // Retiree (<65)
  3: { employee: 0.0415, employer: 0.0615 }, // Senior (65-69)
  4: { employee: 0, employer: 0.02 }, // Senior Retiree
  5: { employee: 0.0415, employer: 0.0615 }, // Elderly (≥70)
  6: { employee: 0, employer: 0.02 }, // Elderly Retiree
  7: { employee: 0, employer: 0.02 }, // Summer Student
};

// NIB Status Display Helpers
export const NIB_STATUS_COLORS: Record<NIBSubmissionStatus, string> = {
  DRAFT: 'gray',
  VALIDATING: 'blue',
  VALIDATED: 'amber',
  GENERATING: 'blue',
  GENERATED: 'green',
  SUBMITTED: 'purple',
  FAILED: 'red',
};

export const NIB_STATUS_LABELS: Record<NIBSubmissionStatus, string> = {
  DRAFT: 'Draft',
  VALIDATING: 'Validating...',
  VALIDATED: 'Validated',
  GENERATING: 'Generating...',
  GENERATED: 'Generated',
  SUBMITTED: 'Submitted',
  FAILED: 'Failed',
};

export const NIB_FORMAT_LABELS: Record<NIBFormatType, string> = {
  NON_HOSPITALITY: 'Non-Hospitality (79 bytes)',
  HOSPITALITY: 'Hospitality (115 bytes)',
};

export const NIB_EMPLOYEE_TYPE_LABELS: Record<NIBEmployeeType, string> = {
  REGULAR: 'Regular Employee',
  SUMMER_STUDENT: 'Summer Student',
  RETIREE: 'Retiree',
};

export const NIB_PAY_INDICATOR_LABELS: Record<NIBPayIndicator, string> = {
  W: 'Weekly',
  B: 'Bi-weekly',
  S: 'Semi-monthly',
  M: 'Monthly',
};

// ==================== Utility Types ====================

export interface DateRange {
  start: string;
  end: string;
}

export interface SelectOption<T = string> {
  label: string;
  value: T;
}
